import { z } from 'zod';
import { HI_TYPES } from '../constants';
import { buildPatient } from '../fhir/build';
import { checkDocument } from '../fhir/check';
import { readPrescription } from '../fhir/read';
import { documentDateOf } from '../fhir/summary';
import type { Bundle } from '../fhir/types';
import type { DeviceKeys, PeerDevice } from '../keys';
import { jsonBytes, sealEnvelope } from '../seal';
import { withStoreLock } from './engine';
import { MAX_RECORD_BYTES, categoryOfDocument, type HeldRecord, type RecordStore } from './records';
import { ShareRefusal, sharedRecordId } from './share';

const objectId = z.string().regex(/^[a-f0-9]{24}$/, 'Must be an ID');

/**
 * What a prescription or care_record envelope carries: one document a
 * practice made, as a copy for the patient or a prescription for a pharmacy.
 * The practice is the one the sender names; receivers check it against what
 * they know, and a prescription also names it inside the signed document.
 */
export const deliveryPayloadSchema = z.strictObject({
  v: z.literal(1),
  practice: z.strictObject({ id: objectId, name: z.string().min(1).max(120) }),
  record: z.strictObject({
    /** The practice's own ID and version for the document, so a resend updates the copy. */
    id: z.uuid(),
    version: z.number().int().min(1),
    hiType: z.enum(HI_TYPES),
    resource: z.record(z.string(), z.unknown()),
  }),
});
export type DeliveryPayload = z.infer<typeof deliveryPayloadSchema>;

/** The payload for one held document of a practice. */
export function deliveryOf(record: Pick<HeldRecord, 'id' | 'version' | 'hiType' | 'resource'>, practice: { id: string; name: string }): DeliveryPayload {
  if (!record.hiType) throw new Error('Only documents can be delivered');
  return {
    v: 1,
    practice: { id: practice.id, name: practice.name.slice(0, 120) },
    record: { id: record.id, version: record.version, hiType: record.hiType, resource: record.resource },
  };
}

/** Seals one envelope of the payload for each recipient device. */
export async function sealDeliveries(input: {
  kind: 'prescription' | 'care_record';
  sender: DeviceKeys;
  payload: DeliveryPayload;
  recipients: PeerDevice[];
  /** Set when the recipients are a pharmacy's record holder devices. */
  toPractice?: string;
}): Promise<{ header: string; ciphertext: string; signature: string }[]> {
  const wires: { header: string; ciphertext: string; signature: string }[] = [];
  for (const recipient of input.recipients) {
    const sealed = await sealEnvelope({
      kind: input.kind,
      sender: input.sender,
      recipient,
      plaintext: jsonBytes(input.payload),
      ...(input.toPractice ? { toPractice: input.toPractice } : {}),
      hiType: input.payload.record.hiType,
    });
    wires.push(sealed.wire);
  }
  return wires;
}

/** The ID a receiver gives a delivered document: the same for every resend of that practice's record. */
export function deliveredRecordId(practiceId: string, recordId: string): Promise<string> {
  return sharedRecordId(`practice:${practiceId}`, recordId);
}

const byteLength = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

/**
 * Files a prescription at a pharmacy's record holder, after the caller has
 * checked the doctor's signature on it. Everything filed comes from the
 * signed document: the prescribing practice from its custodian, and the
 * patient from its subject. A pharmacy keeps one patient record per Curentia
 * account, or per clinic record number for a patient without one, so repeat
 * prescriptions gather under one patient. Copies cannot be changed by
 * members, and a resend of a version already filed changes nothing.
 */
export async function takePrescription(
  store: RecordStore,
  input: { payload: DeliveryPayload; senderId: string; forwarded: boolean; now: Date },
): Promise<HeldRecord> {
  const { record } = input.payload;
  if (record.hiType !== 'Prescription') throw new ShareRefusal('Only prescriptions go to a pharmacy');
  const [issue] = checkDocument(record.resource, 'Prescription');
  if (issue) throw new ShareRefusal(`The document is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
  if (byteLength(record.resource) > MAX_RECORD_BYTES) throw new ShareRefusal('The document is too large');
  const bundle = record.resource as unknown as Bundle;
  const view = readPrescription(bundle);
  const practiceId = view.practice?.id;
  if (!practiceId || !view.id) throw new ShareRefusal('The prescription does not name its practice');
  const date = documentDateOf(bundle) ?? input.now.toISOString();

  // A stable key for the patient: their account, or the prescribing practice's record number.
  const subject = bundle.entry?.find((e) => e.resource?.resourceType === 'Patient')?.resource;
  const recordNumber = subject?.resourceType === 'Patient' ? subject.identifier?.find((i) => i.type?.coding?.some((c) => c.code === 'MR'))?.value : undefined;
  const key = view.patient.accountId ? `account:${view.patient.accountId}` : recordNumber ? `record:${practiceId}:${recordNumber}` : `document:${view.id}`;
  const patientId = await sharedRecordId('pharmacy-patient', key);
  const id = await deliveredRecordId(practiceId, view.id);
  const at = input.now.toISOString();

  return withStoreLock(store, async () => {
    const existing = await store.get(id);
    if (existing && (existing.source?.version ?? 0) >= record.version) return existing;
    if (existing && existing.patientId !== patientId) throw new ShareRefusal('The prescription names a different patient than before');
    if (!(await store.get(patientId))) {
      const patient = buildPatient({
        name: view.patient.name || 'Unnamed patient',
        ...(view.patient.gender ? { gender: view.patient.gender } : {}),
        ...(view.patient.birthDate ? { birthDate: view.patient.birthDate } : {}),
        ...(view.patient.mobile ? { mobile: view.patient.mobile } : {}),
        ...(view.patient.accountId
          ? { accountId: view.patient.accountId }
          : { medicalRecord: { value: recordNumber ?? view.id, assigner: view.practice?.name ?? 'Prescribing practice' } }),
      });
      await store.save({
        id: patientId,
        patientId,
        kind: 'patient',
        category: 'demographics',
        version: 1,
        resource: patient as unknown as Record<string, unknown>,
        ...(view.patient.accountId ? { accountId: view.patient.accountId } : {}),
        createdAt: at,
        createdBy: input.senderId,
        updatedAt: at,
        updatedBy: input.senderId,
      });
    }
    return store.save({
      id,
      patientId,
      kind: 'document',
      category: categoryOfDocument('Prescription'),
      hiType: 'Prescription',
      version: (existing?.version ?? 0) + 1,
      resource: record.resource,
      date,
      source: {
        from: 'practice',
        practiceId,
        ...(view.practice?.name ? { practiceName: view.practice.name.slice(0, 120) } : {}),
        ...(input.forwarded ? { forwarded: true as const } : {}),
        recordId: record.id,
        version: record.version,
        receivedAt: at,
      },
      createdAt: existing?.createdAt ?? at,
      createdBy: existing?.createdBy ?? input.senderId,
      updatedAt: at,
      updatedBy: input.senderId,
    });
  });
}
